import { computed, inject, Service, signal, Signal } from '@angular/core';
import { MgsWebApi } from '../mgs-webapi.service';
import { firstValueFrom } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { programmContentDefs } from './programm-cms-def';
import { partnerContentDefs } from './partner-cms-def';
import { CmsContentDef } from './cms-content-def';
import { ICmsContentPart } from './cms-content-part/cms-content-part.types';
import { ICmsContent, ICmsContentList } from '@mng/mgs-lib';
import { IOption, MngDialogService } from '@mng/mng-inputs';
import { dateToIsoDateString, getBeginningOfNextMonth, getFormattedDateString } from '@mng/mng-ts-helpers';
import { CmsContent, PictureDto } from '@mng/ego4you';

const contextContentDefs: { [context: string]: CmsContentDef[] } = {
  partner: partnerContentDefs,
  programm: programmContentDefs
};

@Service()
export class CmsContentService {
  readonly webApi = inject(MgsWebApi);
  readonly sanitizer = inject(DomSanitizer);
  readonly cmsContentPreview = signal<SafeHtml>(this.sanitizer.bypassSecurityTrustHtml(''));

  // ist signal und NICHT computed da es auch direkt manipuliert wird
  readonly cmsContent = signal<CmsContent>({});

  readonly context = signal<string>('');

  readonly cmsContentList = signal<ICmsContentList>(undefined);

  readonly cmsActiveContent = computed(() => this.cmsContentList()?.activeContent);

  readonly dialogService = inject(MngDialogService);

  readonly versionOptions = computed(() =>
    this.cmsContentList()?.contentHistory.map(
      (history) => ({ value: history.id, name: getFormattedDateString(history.valid_from) }) as IOption
    )
  );

  readonly contentDefs: Signal<CmsContentDef[]> = computed(() => contextContentDefs[this.context()] ?? []);

  async load(context: string, id: number) {
    const cmsContentList = (await firstValueFrom(this.webApi.cmsHistory(context, id))).data.result;
    this.context.set(context);
    this.handlCmsContentList(cmsContentList);
  }

  // Eine konkrete Version laden
  async loadCmsById(id: number) {
    const cmsContentList = (await firstValueFrom(this.webApi.cmsDetail(id))).data.result;
    this.handlCmsContentList(cmsContentList);
    await this.updatedPreviewWithCurrentContent();
  }

  handlCmsContentList(cmsContentList: ICmsContentList) {
    const cmsContentObject =
      cmsContentList.activeContent.cmsContent?.length > 0 ? JSON.parse(cmsContentList.activeContent.cmsContent) : {};
    delete cmsContentList.activeContent.cmsContent;
    this.cmsContentList.set(cmsContentList);
    this.cmsContent.set(cmsContentObject);
  }

  async detailForRendering(context: string, id: number) {
    const htmlString = await this.webApi.cmsDetailForRendering(context, id);
    this.updatePreview(htmlString ?? '');
  }

  updateContent(def: CmsContentDef, contentPart: ICmsContentPart, value: string | PictureDto | undefined) {
    const updated = { ...this.cmsContent() };

    if (!updated[def.id]) {
      updated[def.id] = {};
    }
    updated[def.id][contentPart.key] = value;

    this.cmsContent.set(updated);
    void this.updatedPreviewWithCurrentContent();
  }

  async updatedPreviewWithCurrentContent() {
    const htmlString = (
      await firstValueFrom(
        this.webApi.cmsDetailForPreview(
          this.cmsContentList().activeContent.entityId,
          this.cmsContentList().activeContent.context,
          this.cmsContent()
        )
      )
    ).data.result;
    this.updatePreview(htmlString ?? '');
  }

  updatePreview(htmlString: string = '') {
    this.cmsContentPreview.set(this.sanitizer.bypassSecurityTrustHtml(htmlString));
  }

  async speichern(input: Partial<ICmsContent>) {
    const cmsContent = JSON.stringify(this.cmsContent());
    let updated: ICmsContent = { ...this.cmsActiveContent(), ...input, cmsContent };

    updated = (await firstValueFrom(this.webApi.cmsDetailSave(updated))).data.result;

    await this.loadCmsById(updated.id);

    this.cmsContentList.update((cmsContentList) => ({ ...cmsContentList, activeContent: updated }));
  }

  neueVersion(kopieren = true) {
    const activeContent = this.createNewVersion(kopieren ? this.cmsActiveContent().cmsContent : '');
    this.cmsContentList.update((contentList) => ({ ...contentList, activeContent }));
    if (!kopieren) {
      this.cmsContent.set({});
    }
    void this.updatedPreviewWithCurrentContent();
  }

  versionKopieren() {
    this.neueVersion(true);
  }

  versionEntfernen() {
    this.dialogService.showYesNo({ htmlContent: 'Möchten Sie die CMS Daten dieser Version löschen?' }, async () => {
      const cmsContentList = (await firstValueFrom(this.webApi.cmsDetailEntfernen(this.cmsActiveContent().id))).data
        .result;
      this.handlCmsContentList(cmsContentList);
      await this.updatedPreviewWithCurrentContent();
    });
  }

  private createNewVersion(cmsContent = ''): ICmsContent {
    const neu: ICmsContent = {
      ...this.cmsActiveContent(),
      cmsContent,
      id: undefined,
      version: undefined,
      created_at: undefined,
      comment: undefined,
      valid_from: dateToIsoDateString(getBeginningOfNextMonth())
    };
    return neu;
  }
}
